import React from 'react'
import styled, { css } from 'styled-components'

export const TabRadio = ({
  options = [],
  tabStyle = 'button',
  name,
  value,
  onChange,
  tabClassName,
}: TabRadioProps) => {
  return (
    <Container>
      {options.map((option, index) => {
        const isChecked =
          (value !== undefined && value === option.value) || option.isSelected

        // full width the last item if the tabs are odd
        const isFullWidth = (index + 1) % 2 > 0 && index + 1 === options.length

        const handleChange = () => onChange?.(option.value)

        const onClick = option.onClick || handleChange

        const defaultClassName = isFullWidth ? 'col-12' : 'col-12 col-sm-6'

        return (
          <div
            className={tabClassName || defaultClassName}
            key={`Tab--${index}`}
          >
            <Tab
              tabStyle={tabStyle}
              isDisabled={option.disabled}
              isChecked={isChecked}
              onClick={onClick}
            >
              {option.icon || option.label}
              <input
                className="d-none"
                type="radio"
                name={name}
                checked={isChecked}
              />
              <Check isChecked={isChecked} />
            </Tab>
          </div>
        )
      })}
    </Container>
  )
}

export type TabRadioProps = {
  options: {
    value?: any
    key?: string
    label: string
    isSelected?: boolean
    disabled?: boolean
    icon?: JSX.Element | null
    onClick?: () => void
  }[]
  tabStyle?: 'button' | 'tabs'
  name: string
  value?: any
  tabClassName?: string
  onChange?: (value: any) => void
}

const Container = styled.div.attrs({
  className: 'row g-4',
})``

const Tab = styled.div.attrs({
  className: 'p-2',
})<{
  tabStyle?: 'button' | 'tabs'
  isDisabled?: boolean
  isChecked?: boolean
}>`
  border: 0.2rem solid
    ${(props) =>
      props.isChecked
        ? props.theme.palette.silverTree
        : props.theme.palette.disabled};
  color: ${(props) =>
    props.isChecked ? props.theme.palette.primary : props.theme.palette.hint};
  border-radius: 0.4rem;
  font-size: 1.6rem;
  height: 5.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.isChecked ? 'default' : 'pointer')};
  position: relative;

  pointer-events: ${(props) =>
    props.isChecked || props.isDisabled ? 'none' : 'all'};

  -webkit-tap-highlight-color: transparent;

  & svg,
  & img {
    opacity: ${(props) => (props.isChecked ? '1' : '0.8')};
  }

  & img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
  }

  ${(props) =>
    !props.isChecked &&
    css`
      &:hover {
        border-color: ${props.theme.palette.hint};
        color: ${props.theme.palette.hint};

        & svg,
        & img {
          opacity: 0.9;
        }
      }
    `}
`

const Check = styled.span<{ isChecked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 1.8rem;
  top: 1.8rem;
  width: 1.8rem;
  height: 1.8rem;
  border: 0.2rem solid
    ${(props) =>
      props.isChecked
        ? props.theme.palette.silverTree
        : props.theme.palette.disabled};
  border-radius: 50%;

  &:after {
    content: '';
    display: block;
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    background: ${(props) =>
      props.isChecked ? props.theme.palette.silverTree : 'transparent'};
  }
`
